import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Camera, Save, User, Mail, Phone, Globe, Bell, Palette, Clock } from 'lucide-react';

const USER_PROFILES_TABLE_ID = '16056';

interface UserProfile {
  id?: number;
  user_id: number;
  bio: string;
  profile_image: number;
  phone: string;
  department: string;
  preferred_language: string;
  email_notifications: boolean;
  theme_preference: string;
  timezone: string;
  created_at?: string;
  updated_at?: string;
}

interface UserInfo {
  ID: number;
  Name: string;
  Email: string;
  CreateTime: string;
}

const UserSettings: React.FC = () => {
  const { toast } = useToast();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [profile, setProfile] = useState<UserProfile>({
    user_id: 0,
    bio: '',
    profile_image: 0,
    phone: '',
    department: '',
    preferred_language: 'en',
    email_notifications: true,
    theme_preference: 'light',
    timezone: 'UTC'
  });
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Load user information and profile
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);

      // Get user info
      const userInfoResponse = await window.ezsite.apis.getUserInfo();
      if (userInfoResponse.error) {
        throw new Error(userInfoResponse.error);
      }

      if (userInfoResponse.data) {
        setUserInfo(userInfoResponse.data);

        // Load user profile from database
        const profileResponse = await window.ezsite.apis.tablePage(USER_PROFILES_TABLE_ID, {
          PageNo: 1,
          PageSize: 1,
          Filters: [
          {
            name: "user_id",
            op: "Equal",
            value: userInfoResponse.data.ID
          }]

        });

        if (profileResponse.error) {
          console.warn('Profile not found, will create new one');
          // Set default profile with user_id
          setProfile((prev) => ({
            ...prev,
            user_id: userInfoResponse.data.ID
          }));
        } else if (profileResponse.data?.List && profileResponse.data.List.length > 0) {
          const profileData = profileResponse.data.List[0];
          setProfile(profileData);

          // Load profile image if exists
          if (profileData.profile_image && profileData.profile_image > 0) {
            setProfileImageUrl(`/api/files/${profileData.profile_image}`);
          }
        } else {
          // Create new profile
          setProfile((prev) => ({
            ...prev,
            user_id: userInfoResponse.data.ID
          }));
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user profile. Please refresh the page.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userInfo) return;

    try {
      setUploading(true);

      const uploadResponse = await window.ezsite.apis.upload({
        filename: file.name,
        file: file
      });

      if (uploadResponse.error) {
        throw new Error(uploadResponse.error);
      }

      if (uploadResponse.data) {
        setProfile((prev) => ({
          ...prev,
          profile_image: uploadResponse.data
        }));
        setProfileImageUrl(`/api/files/${uploadResponse.data}`);

        toast({
          title: 'Success',
          description: 'Profile image uploaded successfully.'
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!userInfo) return;

    try {
      setSaving(true);

      const now = new Date().toISOString();
      const profileData = {
        ...profile,
        user_id: userInfo.ID,
        updated_at: now
      };

      let response;
      if (profile.id) {
        // Update existing profile
        response = await window.ezsite.apis.tableUpdate(USER_PROFILES_TABLE_ID, {
          ID: profile.id,
          ...profileData
        });
      } else {
        // Create new profile
        response = await window.ezsite.apis.tableCreate(USER_PROFILES_TABLE_ID, {
          ...profileData,
          created_at: now
        });
      }

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Success',
        description: 'Profile updated successfully.'
      });

      // Reload profile to get updated data
      await loadUserProfile();

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl" data-id="378wcp682" data-path="src/components/settings/UserSettings.tsx">
        <div className="animate-pulse space-y-6" data-id="6bv2chhbu" data-path="src/components/settings/UserSettings.tsx">
          <div className="h-8 bg-gray-200 rounded w-1/4" data-id="e6wkofaie" data-path="src/components/settings/UserSettings.tsx"></div>
          <div className="h-64 bg-gray-200 rounded" data-id="hahlqzpw4" data-path="src/components/settings/UserSettings.tsx"></div>
          <div className="h-64 bg-gray-200 rounded" data-id="ev9mb45hr" data-path="src/components/settings/UserSettings.tsx"></div>
        </div>
      </div>);

  }

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6" data-id="q8g4giqu5" data-path="src/components/settings/UserSettings.tsx">
      <div className="flex items-center gap-2 mb-6" data-id="lcnlticer" data-path="src/components/settings/UserSettings.tsx">
        <User className="h-6 w-6" data-id="ca9ys7zw2" data-path="src/components/settings/UserSettings.tsx" />
        <h1 className="text-3xl font-bold" data-id="x6qy154tq" data-path="src/components/settings/UserSettings.tsx">User Settings</h1>
      </div>

      {/* Profile Information */}
      <Card data-id="uv7y6ly3p" data-path="src/components/settings/UserSettings.tsx">
        <CardHeader data-id="v2ybzpiw3" data-path="src/components/settings/UserSettings.tsx">
          <CardTitle className="flex items-center gap-2" data-id="mj75vs66h" data-path="src/components/settings/UserSettings.tsx">
            <User className="h-5 w-5" data-id="05mc41wf5" data-path="src/components/settings/UserSettings.tsx" />
            Profile Information
          </CardTitle>
          <CardDescription data-id="0lh7iv5rz" data-path="src/components/settings/UserSettings.tsx">
            Manage your personal information and profile settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6" data-id="3abirkzu1" data-path="src/components/settings/UserSettings.tsx">
          {/* Profile Image */}
          <div className="flex items-center gap-4" data-id="lduyfnskx" data-path="src/components/settings/UserSettings.tsx">
            <Avatar className="h-20 w-20" data-id="u3b3rul7c" data-path="src/components/settings/UserSettings.tsx">
              <AvatarImage src={profileImageUrl} alt="Profile" data-id="0zy4ekapo" data-path="src/components/settings/UserSettings.tsx" />
              <AvatarFallback className="text-lg" data-id="2mgs8fj8s" data-path="src/components/settings/UserSettings.tsx">
                {userInfo?.Name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div data-id="2l9z5gy4a" data-path="src/components/settings/UserSettings.tsx">
              <Label htmlFor="profile-image" className="cursor-pointer" data-id="1kyd9elm0" data-path="src/components/settings/UserSettings.tsx">
                <Button variant="outline" size="sm" disabled={uploading} asChild data-id="ie6m1yloc" data-path="src/components/settings/UserSettings.tsx">
                  <span data-id="9ek58oh44" data-path="src/components/settings/UserSettings.tsx">
                    <Camera className="h-4 w-4 mr-2" data-id="hli4cve62" data-path="src/components/settings/UserSettings.tsx" />
                    {uploading ? 'Uploading...' : 'Change Photo'}
                  </span>
                </Button>
              </Label>
              <Input
                id="profile-image"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden" data-id="urmc0or66" data-path="src/components/settings/UserSettings.tsx" />

              <p className="text-sm text-muted-foreground mt-1" data-id="cy687qvsk" data-path="src/components/settings/UserSettings.tsx">
                JPG, PNG, or GIF (max 5MB)
              </p>
            </div>
          </div>

          <Separator data-id="lqkl4nz1r" data-path="src/components/settings/UserSettings.tsx" />

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="2vxywg7ge" data-path="src/components/settings/UserSettings.tsx">
            <div className="space-y-2" data-id="9m9zx74gi" data-path="src/components/settings/UserSettings.tsx">
              <Label data-id="783cv32l3" data-path="src/components/settings/UserSettings.tsx">Name</Label>
              <Input value={userInfo?.Name || ''} disabled data-id="pg4rou5fk" data-path="src/components/settings/UserSettings.tsx" />
              <p className="text-sm text-muted-foreground" data-id="qr3bmh8ys" data-path="src/components/settings/UserSettings.tsx">
                Contact support to change your name
              </p>
            </div>
            <div className="space-y-2" data-id="n3n1yvisn" data-path="src/components/settings/UserSettings.tsx">
              <Label data-id="8tak84ac0" data-path="src/components/settings/UserSettings.tsx">Email</Label>
              <Input value={userInfo?.Email || ''} disabled data-id="fm134ledr" data-path="src/components/settings/UserSettings.tsx" />
              <p className="text-sm text-muted-foreground" data-id="bflkg32am" data-path="src/components/settings/UserSettings.tsx">
                Contact support to change your email
              </p>
            </div>
          </div>

          {/* Extended Profile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="8v7fle17w" data-path="src/components/settings/UserSettings.tsx">
            <div className="space-y-2" data-id="2qqmtuh1u" data-path="src/components/settings/UserSettings.tsx">
              <Label htmlFor="phone" data-id="jy99tnef2" data-path="src/components/settings/UserSettings.tsx">Phone Number</Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={(e) => setProfile((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 123-4567" data-id="ykmlsimez" data-path="src/components/settings/UserSettings.tsx" />

            </div>
            <div className="space-y-2" data-id="csiqzeogv" data-path="src/components/settings/UserSettings.tsx">
              <Label htmlFor="department" data-id="kezvew2y9" data-path="src/components/settings/UserSettings.tsx">Department</Label>
              <Input
                id="department"
                value={profile.department}
                onChange={(e) => setProfile((prev) => ({ ...prev, department: e.target.value }))}
                placeholder="Mathematics, Science, etc." data-id="xfqdyks2x" data-path="src/components/settings/UserSettings.tsx" />

            </div>
          </div>

          <div className="space-y-2" data-id="tp0wd0gnv" data-path="src/components/settings/UserSettings.tsx">
            <Label htmlFor="bio" data-id="vxhzmo4eg" data-path="src/components/settings/UserSettings.tsx">Biography</Label>
            <Textarea
              id="bio"
              value={profile.bio}
              onChange={(e) => setProfile((prev) => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell us about yourself..."
              rows={4} data-id="dne7y6jyr" data-path="src/components/settings/UserSettings.tsx" />

          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card data-id="ip2rnsol5" data-path="src/components/settings/UserSettings.tsx">
        <CardHeader data-id="3d1sxj6v7" data-path="src/components/settings/UserSettings.tsx">
          <CardTitle className="flex items-center gap-2" data-id="r6hrct4k3" data-path="src/components/settings/UserSettings.tsx">
            <Palette className="h-5 w-5" data-id="t4nd9d4gz" data-path="src/components/settings/UserSettings.tsx" />
            Preferences
          </CardTitle>
          <CardDescription data-id="cmqfd6nqr" data-path="src/components/settings/UserSettings.tsx">
            Customize your app experience and notification settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6" data-id="a5vac32uu" data-path="src/components/settings/UserSettings.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="gnlitqjd8" data-path="src/components/settings/UserSettings.tsx">
            <div className="space-y-2" data-id="m5xrhv8nl" data-path="src/components/settings/UserSettings.tsx">
              <Label data-id="c781kmtdc" data-path="src/components/settings/UserSettings.tsx">Preferred Language</Label>
              <Select
                value={profile.preferred_language}
                onValueChange={(value) => setProfile((prev) => ({ ...prev, preferred_language: value }))} data-id="45ejr7y78" data-path="src/components/settings/UserSettings.tsx">

                <SelectTrigger data-id="4xp88cfqu" data-path="src/components/settings/UserSettings.tsx">
                  <SelectValue data-id="qm6ubed9v" data-path="src/components/settings/UserSettings.tsx" />
                </SelectTrigger>
                <SelectContent data-id="vj62t27w7" data-path="src/components/settings/UserSettings.tsx">
                  <SelectItem value="en" data-id="92fbd8xgm" data-path="src/components/settings/UserSettings.tsx">English</SelectItem>
                  <SelectItem value="es" data-id="slx65d94d" data-path="src/components/settings/UserSettings.tsx">Español</SelectItem>
                  <SelectItem value="fr" data-id="k4aqbw1zy" data-path="src/components/settings/UserSettings.tsx">Français</SelectItem>
                  <SelectItem value="de" data-id="s03jva7pr" data-path="src/components/settings/UserSettings.tsx">Deutsch</SelectItem>
                  <SelectItem value="zh" data-id="qwtqcqa4a" data-path="src/components/settings/UserSettings.tsx">中文</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-id="a4z1qa4ie" data-path="src/components/settings/UserSettings.tsx">
              <Label data-id="x62ufwae0" data-path="src/components/settings/UserSettings.tsx">Theme</Label>
              <Select
                value={profile.theme_preference}
                onValueChange={(value) => setProfile((prev) => ({ ...prev, theme_preference: value }))} data-id="ygxlz4kwj" data-path="src/components/settings/UserSettings.tsx">

                <SelectTrigger data-id="shs3z8nvy" data-path="src/components/settings/UserSettings.tsx">
                  <SelectValue data-id="9jun440zi" data-path="src/components/settings/UserSettings.tsx" />
                </SelectTrigger>
                <SelectContent data-id="dba0jjyhx" data-path="src/components/settings/UserSettings.tsx">
                  <SelectItem value="light" data-id="v9a3luj9y" data-path="src/components/settings/UserSettings.tsx">Light</SelectItem>
                  <SelectItem value="dark" data-id="w7dvl3ucy" data-path="src/components/settings/UserSettings.tsx">Dark</SelectItem>
                  <SelectItem value="auto" data-id="wlq4fuu4x" data-path="src/components/settings/UserSettings.tsx">Auto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2" data-id="cf2crbltj" data-path="src/components/settings/UserSettings.tsx">
            <Label data-id="lfkggczrb" data-path="src/components/settings/UserSettings.tsx">Timezone</Label>
            <Select
              value={profile.timezone}
              onValueChange={(value) => setProfile((prev) => ({ ...prev, timezone: value }))} data-id="4rpkkj1o3" data-path="src/components/settings/UserSettings.tsx">

              <SelectTrigger data-id="2gieieoy2" data-path="src/components/settings/UserSettings.tsx">
                <SelectValue data-id="6b4clzzat" data-path="src/components/settings/UserSettings.tsx" />
              </SelectTrigger>
              <SelectContent data-id="uicngxytw" data-path="src/components/settings/UserSettings.tsx">
                <SelectItem value="UTC" data-id="fb6kec8ot" data-path="src/components/settings/UserSettings.tsx">UTC</SelectItem>
                <SelectItem value="America/New_York" data-id="x6wcaa8r9" data-path="src/components/settings/UserSettings.tsx">Eastern Time</SelectItem>
                <SelectItem value="America/Chicago" data-id="3pw18btrm" data-path="src/components/settings/UserSettings.tsx">Central Time</SelectItem>
                <SelectItem value="America/Denver" data-id="oo92uphfy" data-path="src/components/settings/UserSettings.tsx">Mountain Time</SelectItem>
                <SelectItem value="America/Los_Angeles" data-id="g0xva8v91" data-path="src/components/settings/UserSettings.tsx">Pacific Time</SelectItem>
                <SelectItem value="Europe/London" data-id="jtoua9wrb" data-path="src/components/settings/UserSettings.tsx">GMT</SelectItem>
                <SelectItem value="Europe/Paris" data-id="tzmkjwjcy" data-path="src/components/settings/UserSettings.tsx">CET</SelectItem>
                <SelectItem value="Asia/Tokyo" data-id="r7gd7ry2x" data-path="src/components/settings/UserSettings.tsx">JST</SelectItem>
                <SelectItem value="Asia/Shanghai" data-id="8b5b3xinp" data-path="src/components/settings/UserSettings.tsx">CST</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator data-id="7g4kouppf" data-path="src/components/settings/UserSettings.tsx" />

          <div className="space-y-4" data-id="bm4f1z4ij" data-path="src/components/settings/UserSettings.tsx">
            <h4 className="font-medium flex items-center gap-2" data-id="u9ale2lhb" data-path="src/components/settings/UserSettings.tsx">
              <Bell className="h-4 w-4" data-id="ngrk1mq7e" data-path="src/components/settings/UserSettings.tsx" />
              Notifications
            </h4>
            <div className="flex items-center justify-between" data-id="6hnf7id0c" data-path="src/components/settings/UserSettings.tsx">
              <div data-id="kaja9i8nv" data-path="src/components/settings/UserSettings.tsx">
                <Label data-id="zs4dj7z9z" data-path="src/components/settings/UserSettings.tsx">Email Notifications</Label>
                <p className="text-sm text-muted-foreground" data-id="rvdc6pnpu" data-path="src/components/settings/UserSettings.tsx">
                  Receive updates and reminders via email
                </p>
              </div>
              <Switch
                checked={profile.email_notifications}
                onCheckedChange={(checked) => setProfile((prev) => ({ ...prev, email_notifications: checked }))} data-id="ajr6sem80" data-path="src/components/settings/UserSettings.tsx" />

            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card data-id="p2rjnahzq" data-path="src/components/settings/UserSettings.tsx">
        <CardHeader data-id="9wyyqtd3i" data-path="src/components/settings/UserSettings.tsx">
          <CardTitle className="flex items-center gap-2" data-id="smirzpvmk" data-path="src/components/settings/UserSettings.tsx">
            <Clock className="h-5 w-5" data-id="uds29wf4c" data-path="src/components/settings/UserSettings.tsx" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent data-id="w4q27k3dl" data-path="src/components/settings/UserSettings.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="9zxe7nvfg" data-path="src/components/settings/UserSettings.tsx">
            <div data-id="jkasq601u" data-path="src/components/settings/UserSettings.tsx">
              <Label className="text-sm font-medium" data-id="dhkbvnle2" data-path="src/components/settings/UserSettings.tsx">Member Since</Label>
              <p className="text-sm text-muted-foreground" data-id="9ebsnrye2" data-path="src/components/settings/UserSettings.tsx">
                {userInfo?.CreateTime ? new Date(userInfo.CreateTime).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
            <div data-id="gdlk45vr9" data-path="src/components/settings/UserSettings.tsx">
              <Label className="text-sm font-medium" data-id="fkp1uqyix" data-path="src/components/settings/UserSettings.tsx">User ID</Label>
              <p className="text-sm text-muted-foreground" data-id="jdkszg56d" data-path="src/components/settings/UserSettings.tsx">{userInfo?.ID}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end" data-id="0hzh2gif7" data-path="src/components/settings/UserSettings.tsx">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="min-w-32" data-id="on110v88w" data-path="src/components/settings/UserSettings.tsx">

          <Save className="h-4 w-4 mr-2" data-id="1e9nlvins" data-path="src/components/settings/UserSettings.tsx" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>);

};

export default UserSettings;